import { Box, type SxProps, type Theme } from "@mui/material";
import type { FC, ReactNode } from "react";
import { MOTION } from "../../../theme/tokens";

/** The class the container uses to address the bar; exported only through `CHAT_ACTION_BAR_REVEAL_SX`. */
const ROOT_CLASS = "ChatActionBar-root";

/**
 * The class the container uses to address a `ChatActionBarSwap` — content that SHARES the bar's slot and
 * has to step aside as the bar appears. Kept here, next to `ROOT_CLASS`, so one selector block owns both
 * halves of the swap and they can never drift out of sync.
 */
export const CHAT_ACTION_BAR_SWAP_CLASS = "ChatActionBarSwap-root";

const REVEALED = { opacity: 1, pointerEvents: "auto" as const };
/**
 * Visual-only yielding: `opacity` (never `visibility`/`display`) so the swapped-out content keeps its box,
 * its text content and its place in the accessibility tree — the reveal must cost no information, and a
 * constant box is what makes the swap shift nothing.
 */
const YIELDED = { opacity: 0, pointerEvents: "none" as const };

/**
 * Spread onto the surface that owns the answer (its bubble/card) to reveal the bar while that surface is
 * hovered or holds focus. It lives with the container, not the bar: an `opacity: 0` bar takes no pointer
 * events, so it can never reveal itself on its own hover.
 *
 * The same conditions hide any `ChatActionBarSwap` inside that surface, so a bar that shares its slot with
 * resting content trades places with it under one rule rather than two that could disagree.
 */
export const CHAT_ACTION_BAR_REVEAL_SX: SxProps<Theme> = {
	[`&:focus-within .${ROOT_CLASS}`]: REVEALED,
	[`&:focus-within .${CHAT_ACTION_BAR_SWAP_CLASS}`]: YIELDED,
	"@media (hover: hover) and (pointer: fine)": {
		[`&:hover .${ROOT_CLASS}`]: REVEALED,
		[`&:hover .${CHAT_ACTION_BAR_SWAP_CLASS}`]: YIELDED,
	},
};

/** Base state of swapped content: present and opaque, fading on the same curve the bar fades in on. */
export const CHAT_ACTION_BAR_SWAP_SX: SxProps<Theme> = (theme) => ({
	opacity: 1,
	transition: theme.transitions.create("opacity", { duration: MOTION.DURATION.HOVER, easing: MOTION.EASE_OUT }),
});

type Props = {
	children: ReactNode;
	/**
	 * Keep the bar visible regardless of hover/focus — for an answer whose actions carry state worth
	 * seeing at rest (a rating already given, a save in flight).
	 */
	alwaysVisible?: boolean;
	/**
	 * Whether a coarse pointer — touch, where there is no hover to reveal anything — shows the bar at rest.
	 * Default `true`, which is what makes the actions reachable at all on a touch screen.
	 *
	 * Pass `false` only when the bar SHARES its slot with a `ChatActionBarSwap` whose content must stay
	 * readable on touch: "always shown" and "shares its slot" cannot both hold, so the bar then reveals on
	 * focus only and the resting content keeps the slot. The caller owes the user another route to those
	 * actions on touch (the monthly day cell has the day sidebar's identical header row).
	 */
	showOnCoarsePointer?: boolean;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/**
 * The row of per-answer actions under a chat message — copy, regenerate, rate. It always holds its place
 * (no layout shift when it appears) but stays hidden until the answer is hovered or focused; on
 * touch/coarse pointers, where there is no hover, it is simply always shown.
 *
 * Pair with `CHAT_ACTION_BAR_REVEAL_SX` on the containing bubble.
 */
export const ChatActionBar: FC<Props> = ({ children, alwaysVisible = false, showOnCoarsePointer = true, dataTestId, sx }) => (
	<Box
		className={ROOT_CLASS}
		data-testid={dataTestId}
		sx={[
			(theme) => ({
				display: "flex",
				alignItems: "center",
				gap: 0.25,
				mt: 0.5,
				opacity: alwaysVisible ? 1 : 0,
				pointerEvents: alwaysVisible ? "auto" : "none",
				transition: theme.transitions.create("opacity", { duration: MOTION.DURATION.HOVER, easing: MOTION.EASE_OUT }),
				// Outranks the coarse-pointer rule below on specificity, so focus still reveals the bar on a
				// touch screen even where `showOnCoarsePointer` keeps it hidden at rest.
				"&:focus-within": REVEALED,
				...(showOnCoarsePointer ? { "@media (hover: none), (pointer: coarse)": REVEALED } : {}),
			}),
			...(Array.isArray(sx) ? sx : [sx]),
		]}
	>
		{children}
	</Box>
);
